import { AssignmentOutlined, LinkOutlined } from "@mui/icons-material";
import { Button, Link, Tooltip } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AssociationsEditor from "../../../associations/AssociationsEditor";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { formatBorrowerObjectFullName } from "../../../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

const AssignLoanFilesButton = ({
  borrower,
  property,
  entity,
  variant = "outlined",
  color = "gray",
  size = "small",
  sx,
  tooltip,
  startIcon = <LinkOutlined sx={{ mx: 0.5 }} className="thin" />,
  // startIcon = <AssignmentOutlined sx={{ mx: 0.5 }} className="thin" />,
  children,
  ...rest
}) => {
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, associateDocumentWithObjects } = useLoanFilesHook();

  const targetKind = property ? "property" : entity ? "entity" : "borrower";
  const tooltipTitle = tooltip
    ? tooltip
    : targetKind === "property"
      ? "Assign Files to Property"
      : targetKind === "entity"
        ? "Assign Files to Entity"
        : "Assign Files to Borrower";
  const title =
    targetKind === "property"
      ? "Assign files to property"
      : targetKind === "entity"
        ? "Assign files to entity"
        : "Assign files to borrower";

  // Host label shown next to the editor title — gives the user a clear
  // anchor for what they're linking files against.
  const hostLabel = useMemo(() => {
    if (property) {
      return (
        addressLabel(property?.address) ||
        property?.address?.streetAddress ||
        "Property"
      );
    }
    if (entity) return entity?.entityName || "Entity";
    if (borrower) return formatBorrowerObjectFullName(borrower) || "Borrower";
    return undefined;
  }, [property, entity, borrower]);

  // Pre-checked + locked rows in the editor: whatever loanDocument ids are
  // already associated with the target.
  const existing = useMemo(() => {
    const linked =
      property?.associations?.loanDocuments ||
      entity?.associations?.loanDocuments ||
      borrower?.associations?.loanDocuments ||
      [];
    return { loanDocuments: linked };
  }, [property, entity, borrower]);

  const handleSave = useCallback(
    (selected) => {
      const newDocIds = selected?.loanDocuments || [];
      if (newDocIds.length === 0) {
        setShow(false);
        return;
      }
      associateDocumentWithObjects({
        dealId: loanDrilldown?._id,
        loanDocumentsIds: newDocIds,
        borrowerIds: borrower?._id ? [borrower._id] : undefined,
        entityIds: entity?._id ? [entity._id] : undefined,
        subjectPropertyIds: property?._id ? [property._id] : undefined,
        onSuccessFn: (d) => {
          dispatch(loanDrilldownSet(d));
          setShow(false);
        },
      });
    },
    [
      loanDrilldown?._id,
      borrower?._id,
      entity?._id,
      property?._id,
      associateDocumentWithObjects,
      dispatch,
    ],
  );

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <Button
          onClick={() => setShow(true)}
          color={color}
          variant={variant}
          size={size}
          sx={sx}
          startIcon={startIcon}
          {...rest}
        >
          {/* {children || "Assign Files"} */}
          {children || "Link Existing Docs"}
        </Button>
      </Tooltip>
      <AssociationsEditor
        open={show}
        loan={loanDrilldown}
        enabledFields={["loanDocuments"]}
        existing={existing}
        title={title}
        hostLabel={hostLabel}
        saving={loading}
        onSave={handleSave}
        onClose={() => setShow(false)}
      />
    </>
  );
};

export default AssignLoanFilesButton;
